/* eslint-disable no-loops/no-loops */
import { format, formatPrefix } from 'd3-format';
import { Feature, Geometry, Position } from 'geojson';
import { MapGeoJSONFeature } from 'maplibre-gl';
import polylabel from 'polylabel';
import resolveConfig from 'tailwindcss/resolveConfig';

import tailwindConfig from '@/../tailwind.config.js';
import { getCounties } from '@/endpoints/itasca/county/getCounties';
import { AcresFeature } from '@/models/features';

import centroid from '../centroid';
import { getArea } from '../getAcres';
import { isDefined } from '../guards';

/* eslint-disable no-restricted-syntax */
export const prettifyNumber = (number: number, multiplier = 1): number =>
  Math.floor(number * multiplier);

export function nFormatter(
  num: number | null | undefined = 0,
  digits = 1,
  shouldDisplayK = false
): string {
  // Does not add a K to the end of the number, but still rounds to 1 decimal place
  if (!shouldDisplayK && (num || 0) > 0 && (num || 0) < 1000000) {
    return format(`.${digits}f`)(num || 0);
  }

  if (num && num < 1 && typeof num === 'number') return num.toFixed(digits);

  // Formats numbers to be 1 decimal and append a K,M,B to the end of it
  return formatPrefix(`.${digits}`, num || 0)(num || 0).replace(/G/, 'B');
}

export const isValidUrl = (url: string): boolean => {
  try {
    return !!new URL(url);
  } catch (e) {
    return false;
  }
};

export const round = (value: number | null, precision = 0): number => {
  if (!value) return 0;

  const multiplier = 10 ** (precision || 0);

  return Math.round(value * multiplier) / multiplier;
};

export const numberWithCommas = (
  input: number | string | null,
  decimal = false,
  precision = 1
): string => {
  if (input === null || input === '') return 'NA';
  if (decimal) return format(`,.${precision}f`)(input as any);
  return format(',.0f')(input as any);
};

// TODO: Fix the return type on this
export function getUniqueFeatures(
  features: MapGeoJSONFeature[],
  comparatorProperty
): any[] {
  const uniqueIds = new Set();
  const uniqueFeatures = [] as any[];

  for (const feature of features) {
    if (feature.properties) {
      const id = feature[comparatorProperty];
      if (!uniqueIds.has(id)) {
        uniqueIds.add(id);
        uniqueFeatures.push(feature);
      }
    }
  }

  return uniqueFeatures;
}

export function getUniqueFeaturesProperties(
  features: MapGeoJSONFeature[] | AcresFeature[],
  comparatorProperty
) {
  const uniqueIds = new Set();
  const uniqueFeatures = [] as any[];

  for (const feature of features) {
    if (feature.properties) {
      const id = feature.properties[comparatorProperty];
      if (!uniqueIds.has(id)) {
        uniqueIds.add(id);
        uniqueFeatures.push(feature);
      }
    }
  }

  return uniqueFeatures;
}

export function getUniqueValues(list: any[], comparatorProperty: any): any[] {
  const uniqueValue = new Set();
  const uniqueObjects = [] as any[];

  for (const item of list) {
    const value = item[comparatorProperty];

    if (!uniqueValue.has(value)) {
      uniqueValue.add(value);
      uniqueObjects.push(item);
    }
  }

  return uniqueObjects;
}

export function findPolylabel(feature: Feature) {
  let output: number[] = [];

  if (feature.geometry.type === 'Polygon') {
    output = polylabel(feature.geometry.coordinates);
  } else if (feature.geometry.type === 'MultiPolygon') {
    let maxArea = 0;
    let maxPolygon: Position[][] = [[]];

    for (let i = 0, l = feature.geometry.coordinates.length; i < l; i += 1) {
      const p = feature.geometry.coordinates[i];
      const area = getArea({ coordinates: p, type: 'Polygon' });
      if (area > maxArea) {
        maxPolygon = p;
        maxArea = area;
      }
    }
    output = polylabel(maxPolygon);
  }

  return output;
}

export function capitalizeFirstLetter(s?: string | null) {
  if (!s) return '';

  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Capitalizes the first letter and lowercases the rest of the letters for every word in the sentence.
 * @param s Sentence
 * @returns
 */
export function capitalizeEveryWord(s?: string | null) {
  if (!s) return '';

  return s
    .replace('_', ', ')
    .split('-')
    .map((item) =>
      item
        .split(' ')
        .map((item2) => capitalizeFirstLetter(item2.toLowerCase()))
        .join(' ')
    )
    .join('-');
}

export function prettyNumberWithCommas(v: number | null = 0): string {
  if (!v) return 'NA';
  if (v === Infinity) return 'NA';
  return `${numberWithCommas(prettifyNumber(v))}`;
}

export function convertCustomId(string: string | number): number {
  return Number(string.toString().split('-')[0]);
}

export function displayRoundedNumberWithCommas(
  number: number,
  decimal?: number
): string {
  const precision = !isDefined(decimal) ? 1 : decimal;

  if (numberWithCommas(nFormatter(number, precision)) !== 'NaN') {
    return numberWithCommas(nFormatter(number, precision), true, precision);
  }
  return nFormatter(number, precision, true);
}

export function convertMoney(
  money: number | null,
  includeCents = false
): string {
  if (!money) return '–';

  if (money === 0) return '–';

  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: includeCents ? 2 : 0,
    minimumFractionDigits: includeCents ? 2 : 0,
    style: 'currency',
  }).format(round(money, includeCents ? 2 : 0));
}

export const fetchCountyAndState = async (
  geometry: Geometry,
  signal?: AbortSignal
) => {
  const c = centroid(geometry);

  if (!c) {
    return {
      county: '',
      state: '',
    };
  }

  const [lng, lat] = c.geometry.coordinates;

  const { res } = await getCounties({ location: { lat, lon: lng } }, signal);
  const resCounty = res?.data.results?.[0];

  if (resCounty) {
    return {
      county: resCounty.name,
      state: resCounty.state_name,
    };
  }

  return {
    county: '',
    state: '',
  };
};

export const extractGeometry = (geometry: string | Geometry): Geometry => {
  if (typeof geometry === 'string') {
    return JSON.parse(geometry) as Geometry;
  }

  return geometry;
};

export function createRandomString(length: number, type?: 'string' | 'number') {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const numbers = '0123456789';

  let charactersLength = 0;
  let activeList = '';

  if (type === 'number') {
    charactersLength = numbers.length;
    activeList = numbers;
  } else {
    charactersLength = characters.length;
    activeList = characters;
  }

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < length; i++) {
    result += activeList.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const cleanCounty = (county: string) => {
  const newCounty = county.replace('County', '');
  return newCounty.trim().replace(/\s+/g, '-');
};

export const getTailwindConfig = () => {
  return resolveConfig(tailwindConfig as any);
};

export const getTailWindColors =
  (): typeof tailwindConfig.theme.extend.colors => {
    const { theme } = getTailwindConfig();

    return theme.colors;
  };
